/**
 * useMe.ts
 * Singleton composable that fetches GET /auth/me once and caches the result
 * in a module-level ref — same pattern as apps/frontend/src/lib/useMe.ts.
 * Used by the layout topbar to show the active company's name.
 */
import { ref } from 'vue';
import type { AuthMeResponse } from '@suite/contracts-kernel';
import { getMe } from './api.js';

const meState = ref<AuthMeResponse | null>(null);
const loadingState = ref(false);
const errorState = ref<string | null>(null);

/** Reset the singleton cache. Call on logout so a second session doesn't leak. */
export function clearMe(): void {
  meState.value = null;
  loadingState.value = false;
  errorState.value = null;
}

export function useMe() {
  async function loadMe(): Promise<void> {
    if (meState.value !== null || loadingState.value === true) return;

    loadingState.value = true;
    errorState.value = null;
    try {
      meState.value = await getMe();
    } catch (err) {
      errorState.value = err instanceof Error ? err.message : 'No se pudo cargar el perfil.';
    } finally {
      loadingState.value = false;
    }
  }

  return {
    me: meState,
    loading: loadingState,
    error: errorState,
    loadMe,
  };
}
